import { useState, useEffect, createContext, useContext} from 'react';
import { Link, useNavigate  } from 'react-router-dom';
import authAPI from '../api/authAPI';


// Login view
function LoginPage() {
  const { user, logout, isLoggedIn } = useAuth();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();


  return (
    <div
      id="wrapper"
      className="flex flex-col h-full items-center justify-center"
    >
      <div>
        <h2 className="text-2xl text-center mb-12 relative text-white">
          {isLoggedIn ? 'Logged in' : (
          <Link to="/">
            <img src="../image/sidekick-logo.png" alt="Sidekick Logo" 
            
            className="w-48"/>
          </Link>
  
        )
          }
        </h2>
      </div>
      {isLoggedIn ? (
        <div className="my-20 text-center">
          <h1 className="text-xl mb-6 text-white">You are logged in as {user?.email}.</h1>
          <p className="my-4 text-lg">
            Go to your <Link to="/profile">profile</Link>
          </p>
          <div className="mt-16">
            <LogoutButton onClick={logout} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
        <LoginForm />
        <div className="w-2/3 min-w-96 mt-4">
            <div className="relative text-center my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative">
                <span className="px-2 text-sm text-white bg-customColor1">New to Sidekick AI?</span>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/signup'}
              className="w-full bg-gray-100 border border-gray-300 hover:bg-gray-200 text-gray-800 font-normal py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Create your Sidekick AI account
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
}

export const LogoutButton = ({ onClick }) => (
  <button
    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 my-2 mx-2 rounded focus:outline-none focus:shadow-outline"
    type="button"
    onClick={onClick}
  >
    Log out
  </button>
);

// Login form
function LoginForm() {
  const [hasError, setHasError] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const forgotPasswordUrl = '#';
  const { login } = useAuth();

  // Login form handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting login with:', { email, password });
      const user = await login({ email, password });
      console.log('Logged in user:', user);
      if (!user) {
        throw new Error('user is empty');
      }
      setHasError(false);
      console.log('logged in');
    } catch (err) {
      console.log('login failed: ' + err.message);
      setHasError(true);
    }
  };

  // Enable the submit button on all fields filled
  useEffect(() => {
    setCanSubmit(email.length > 0 && password.length > 0);
  }, [email, password]);

  return (
    <form
      id="login"
      method="POST"
      action="#"
      className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 w-2/3 min-w-96"
      onSubmit={handleSubmit}
    >
      {hasError && (
        <div className="h-12 warn text-red-500 text-center text-xl">
          There was an error with your login.
        </div>
      )}
      <h1 className="text-3xl font-bold text-center text-[#1E1B3E] mb-2">
        Welcome Back
      </h1>
      <p className="text-center text-gray-600 mb-8">
        Sign in to your Sidekick AI account
      </p>
      <div className="mb-4">
        <label
          className="block text-gray-700 text-sm font-bold mb-2"
          htmlFor="username"
        >
          Username
        </label>
        <input
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          id="username"
          type="text"
          placeholder="Username"
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="mb-6">
        <label
          className="block text-gray-700 text-sm font-bold mb-2"
          htmlFor="password"
        >
          Password
        </label>
        <input
          className={`shadow appearance-none border ${
            hasError ? 'border-red-600' : ''
          } rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline`}
          id="password"
          type="password"
          placeholder="******************"
          onChange={(e) => setPassword(e.target.value)}
        />
        {hasError && (
          <p className="text-red-500 text-xs italic hidden">
            Please choose a password.
          </p>
        )}
      </div>
      <div className="flex flex-col items-center gap-4 mb-4">
        <button
          className={`${
            canSubmit ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300'
          } text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
          type="submit"
          disabled={!canSubmit}
        >
          Sign In
        </button>
        
      </div>
      
      
    </form>
  );
}

const AuthContext = createContext({
  user: undefined,
  token: undefined,
  isLoggedIn: false,
  login: async () => {},
  logout: async () => {},
});

const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState({});
  const [token, setToken] = useState({});

  // Load the previously stored login token when our app first loads.
  // This allows persistent login
  useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('authToken');
    if (user && token) {
      setUser(JSON.parse(user));
      setToken(token);
      setIsLoggedIn(true);
    }
  }, []);

  async function login({ email, password }) {
    console.log(email, password);
    const response = await authAPI.login({ email, password });
    console.log('API response:', response);
    if (!response.success) {
        throw new Error('login failed:', response?.message || 'unknown error');
    }
    const { user, token } = response.data;
    console.log('User:', user);  // Log user data
    console.log('Token:', token);  // Log token
    if (!user || !token) {
        console.error('User or token missing in API response.');
        throw new Error('response user or token is empty');
    }

    // Store both user data and userId
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('authToken', token);
    localStorage.setItem('userId', user.id.toString());

    // When setting the user state, find correct user if it's an array
    if (Array.isArray(user)) {
        const loggedInUser = user.find(u => u.id === parseInt(user.id));
        setUser(loggedInUser);
    } else {
        setUser(user);
    }
    setToken(token);
    setIsLoggedIn(true);

    return user;

  }
  const navigate = useNavigate();

  async function logout() {
    try {
      // Clear user and auth token  from localstorage
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');

      // Clear user and auth token to state
      setUser(null);
      setToken(null);
      setIsLoggedIn(false);
      navigate('/login');

        console.log('Logged out successfully');
    } catch (err) {
        console.log('Logout error:', err);
    }
  }

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a hook we can use to access AuthContext in our components.
const useAuth = () => useContext(AuthContext);

export default function App() {
  return (
    <AuthProvider>
      <LoginPage />
    </AuthProvider>
  );
}